import type { MutableRefObject, RefObject } from 'react';
import { useEffect } from 'react';
import type { ScrollView } from 'react-native';
import type { ScrollView as GestureHandlerScrollView } from 'react-native-gesture-handler';
import type { PinData } from '@sg/garnish';

import type { LocationResultData } from '../../GraphQL';

// ────────────────────────────────────────────────────────────────────────────────

export const useScrollToLocationCard = (
  props: UseScrollToLocationCardProps,
) => {
  const {
    locations,
    locationCardsHeights,
    scrollViewRef,
    selectedLocation,
    isShownLocationWarningModal,
    isShowingLocationDetails,
  } = props;

  // ─── EFFECTS ─────────────────────────────────────────────────────

  useEffect(() => {
    const scrollView = scrollViewRef?.current;

    if (!scrollView || !selectedLocation || isShownLocationWarningModal) return;

    const targetLocationCardScrollTop = calculateScrollTopOffset({
      locations,
      locationCardsHeights,
      selectedLocation,
    });

    scrollView.scrollTo({ y: targetLocationCardScrollTop, animated: true });
  }, [
    locations,
    locationCardsHeights,
    isShownLocationWarningModal,
    isShowingLocationDetails,
    scrollViewRef,
    selectedLocation,
  ]);
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

function calculateScrollTopOffset(
  props: Pick<
    UseScrollToLocationCardProps,
    'locations' | 'locationCardsHeights' | 'selectedLocation'
  >,
) {
  const { locations, locationCardsHeights, selectedLocation } = props;

  const selectedLocationIndex = locations.findIndex(
    ({ id }) => id === selectedLocation,
  );
  const previousLocations = locations.slice(0, selectedLocationIndex);

  const cardHeights = previousLocations.map(({ id }) => {
    const cardHeight = locationCardsHeights.current.get(id);

    return cardHeight ?? 0;
  });

  return cardHeights.reduce(
    (scrollTopOffset, currentCardHeight) => scrollTopOffset + currentCardHeight,
    0,
  );
}

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseScrollToLocationCardProps = {
  // eslint-disable-next-line functional/prefer-readonly-type
  readonly locationCardsHeights: MutableRefObject<Map<string, number>>;
  readonly locations: ReadonlyArray<PinData & LocationResultData>;
  readonly scrollViewRef:
    | RefObject<ScrollView | GestureHandlerScrollView>
    | undefined;
  readonly selectedLocation: string;
  readonly isShownLocationWarningModal?: boolean;
  readonly isShowingLocationDetails?: boolean;
};
